if (!customElements.get('product-card')) {
  class ProductCard extends HTMLElement {
    constructor() {
      super();
      this.colorSwatches = this.querySelectorAll('[data-color-swatch]');
      this.optionInputs = this.querySelectorAll('[data-option-input]');
      this.addToCartForm = this.querySelector('product-form');
      this.addToCartButton = this.querySelector('[data-type="add-to-cart-form"] button');
      this.variantInput = this.querySelector('input[name="id"]');
      this.addCartText = this.addToCartButton ? this.addToCartButton.dataset.addToCartText : null;
      this.soldOutText = this.addToCartButton ? this.addToCartButton.dataset.soldOutText : null;
      this.buttonText = this.querySelector('[data-button-text]');
      this.priceRoot = this.querySelector('.price');
      this.variantImages = this.querySelectorAll('[data-variant-image]');
      this.blockBuilderQtySelectors = this.querySelectorAll('block-builder-qty-selector');
      this.modalTrigger = this.querySelector('[data-modal-trigger]');
      this.productCardLink = this.querySelector('[data-product-card-link]');
      const variantsEl = this.querySelector('[data-variants]');
      this.variantsData = variantsEl ? JSON.parse(variantsEl.textContent) : [];
    }

    connectedCallback() {
      // Attach listeners for color swatches
      if (this.colorSwatches.length) {
        this.colorSwatches.forEach(swatch => {
          swatch.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.handleColorSwatchClick(swatch);
          });
        });
      }

      // Attach listeners for non-color option inputs
      if (this.optionInputs.length) {
        this.optionInputs.forEach(input => {
          input.addEventListener('change', (event) => {
            const selectedValueTextEl = event.target.closest('fieldset')?.querySelector('[data-selected-value]');
            if (selectedValueTextEl) {
              selectedValueTextEl.textContent = event.target.value;
            }
            this.updateFromSelections();
          });
        });
      }

      // Initialize from provided variant id
      if (this.variantsData.length) {
        const initialVariant = this.variantsData.find(v => String(v.id) === String(this.dataset.variantId)) || this.variantsData[0];
        if (initialVariant) {
          this.setSelectionsFromVariant(initialVariant);
          this.updateUiForVariant(initialVariant, { scrollColorIntoView: true });
          this.updateAvailabilityStates();
        } else {
          // Fallback: ensure radios reflect any default checked state
          this.optionInputs.forEach(input => {
            const selectedValueTextEl = input.closest('fieldset')?.querySelector('[data-selected-value]');
            if (selectedValueTextEl && input.checked) {
              selectedValueTextEl.textContent = input.value;
            }
          });
        }
      }

      if (this.dataset.wishlistButtonId) {
        this.wishlistButton = document.querySelector(`#${this.dataset.wishlistButtonId} swym-wishlist-button`);
      }
    }

    handleColorSwatchClick(swatch) {
      if (swatch.disabled || swatch.getAttribute('aria-disabled') === 'true') return;
      // Toggle active state
      this.colorSwatches.forEach(s => s.classList.remove('active'));
      swatch.classList.add('active');
      this.updateFromSelections();
    }

    setSelectionsFromVariant(variant) {
      // Set color active state
      if (this.colorSwatches.length) {
        const colorIndex = parseInt(this.colorSwatches[0].dataset.optionIndex, 10) || 1;
        const variantColor = variant[`option${colorIndex}`];
        const swatch = Array.from(this.colorSwatches).find(s => s.dataset.optionValue === variantColor);
        if (swatch) {
          this.colorSwatches.forEach(s => s.classList.remove('active'));
          swatch.classList.add('active');
        }
      }

      // Set non-color radios
      if (this.optionInputs.length) {
        this.optionInputs.forEach(input => {
          const idx = parseInt(input.dataset.optionIndex, 10) || 1;
          const expected = variant[`option${idx}`];
          input.checked = input.value === expected;
          const selectedValueTextEl = input.closest('fieldset')?.querySelector('[data-selected-value]');
          if (selectedValueTextEl && input.checked) {
            selectedValueTextEl.textContent = input.value;
          }
        });
      }
    }

    getSelectedOptions() {
      const totalOptions = this.variantsData[0]?.options?.length || 3;
      const selections = new Array(totalOptions).fill(null);

      // Non-color options
      this.optionInputs.forEach(input => {
        if (input.checked) {
          const idx = (parseInt(input.dataset.optionIndex, 10) || 1) - 1;
          selections[idx] = input.value;
        }
      });

      // Color option
      if (this.colorSwatches.length) {
        const active = Array.from(this.colorSwatches).find(s => s.classList.contains('active'));
        const idx = (parseInt(this.colorSwatches[0].dataset.optionIndex, 10) || 1) - 1;
        if (active) selections[idx] = active.dataset.optionValue;
      }

      return selections;
    }

    findBestVariantFromSelections(selections) {
      // Strict match
      let variant = this.variantsData.find(v => selections.every((val, i) => val == null || v[`option${i+1}`] === val));
      if (variant) return variant;

      // Relax color: match non-null non-color selections; choose first
      const colorIndex = this.colorSwatches.length ? ((parseInt(this.colorSwatches[0].dataset.optionIndex, 10) || 1) - 1) : null;
      const relaxed = this.variantsData.find(v => selections.every((val, i) => {
        if (val == null) return true;
        if (colorIndex !== null && i === colorIndex) return true;
        return v[`option${i+1}`] === val;
      }));
      return relaxed || this.variantsData[0];
    }

    updateFromSelections(options = {}) {
      if (!this.variantsData.length) return;
      const selections = this.getSelectedOptions();
      const variant = this.findBestVariantFromSelections(selections);

      // If variant color differs from active, sync color selection
      if (this.colorSwatches.length) {
        const colorIndex = parseInt(this.colorSwatches[0].dataset.optionIndex, 10) || 1;
        const requiredColor = variant[`option${colorIndex}`];
        const active = Array.from(this.colorSwatches).find(s => s.classList.contains('active'));
        if (!active || active.dataset.optionValue !== requiredColor) {
          const match = Array.from(this.colorSwatches).find(s => s.dataset.optionValue === requiredColor);
          if (match) {
            this.colorSwatches.forEach(s => s.classList.remove('active'));
            match.classList.add('active');
          }
        }
      }

      this.updateUiForVariant(variant, options);
    }

    updateUiForVariant(variant, { scrollColorIntoView = false, skipAvailability = false } = {}) {
      const variantId = String(variant.id);
      const isAvailable = Boolean(variant.available);

      // Update images
      this.variantImages.forEach(image => {
        if (String(image.dataset.variantImage) === variantId) {
          image.classList.remove('hidden');
          image.classList.add('block');
        } else {
          image.classList.add('hidden');
          image.classList.remove('block');
        }
      });

      // Update variant input
      if (this.variantInput) {
        this.variantInput.value = variantId;
      }

      // Update overlay link
      if (this.productCardLink && this.productCardLink.href) {
        const [base] = this.productCardLink.href.split('?variant=');
        this.productCardLink.href = `${base}?variant=${variantId}`;
      }

      // Update qty selectors
      if (this.blockBuilderQtySelectors.length) {
        this.blockBuilderQtySelectors.forEach(selector => {
          if (String(selector.dataset.variantId) === variantId) {
            selector.classList.remove('hidden');
          } else {
            selector.classList.add('hidden');
          }
        });
      }

      // Modal/wishlist buttons
      if (this.modalTrigger) {
        this.modalTrigger.dataset.variantId = variantId;
      }
      if (this.wishlistButton) {
        this.wishlistButton.dataset.variantId = variantId;
      }

      // Button state
      this.updateButtonState(isAvailable);

      // Price state
      this.updatePrice(variant);

      // Scroll color swatch into view
      if (scrollColorIntoView && this.colorSwatches.length) {
        const active = Array.from(this.colorSwatches).find(s => s.classList.contains('active'));
        if (active) {
          const emblaCarousel = active.closest('embla-carousel');
          if (emblaCarousel && emblaCarousel.emblaApi) {
            const index = Array.from(this.colorSwatches).indexOf(active);
            emblaCarousel.emblaApi.scrollTo(index, true);
          }
        }
      }

      // Update availability after UI sync
      if (!skipAvailability) this.updateAvailabilityStates();
    }

    getEffectiveCompareAtPrice(variant) {
      // First check if the product is actually on sale
      const isOnSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      
      // If not on sale, just return the regular compare_at_price
      if (!isOnSale) {
        return variant.compare_at_price;
      }

      // If on sale, then check for value metafields
      const valuesScript = this.querySelector('[data-product-values]');
      if (!valuesScript) return variant.compare_at_price;

      try {
        const valuesData = JSON.parse(valuesScript.textContent);
        const variantValue = valuesData[`variant_${variant.id}`];
        const productValue = valuesData.product;

        // Convert currency string to number (e.g. "$200.00" -> 20000)
        const convertCurrencyToNumber = (value) => {
          if (!value) return null;
          // Remove currency symbol and any commas, then convert to cents
          return parseInt(value.replace(/[^0-9.-]+/g, '').replace('.', ''), 10);
        };

        const variantValueNum = convertCurrencyToNumber(variantValue);
        const productValueNum = convertCurrencyToNumber(productValue);
        const compareAtPriceNum = variant.compare_at_price;

        // Use the highest value among variant value, product value, and compare at price
        let effectiveCompareAtPrice = compareAtPriceNum || 0;

        if (variantValueNum && variantValueNum > effectiveCompareAtPrice) {
          effectiveCompareAtPrice = variantValueNum;
        } else if (productValueNum && productValueNum > effectiveCompareAtPrice) {
          effectiveCompareAtPrice = productValueNum;
        }

        return effectiveCompareAtPrice || variant.compare_at_price;
      } catch (error) {
        console.error('Error parsing value metafields:', error);
        return variant.compare_at_price;
      }
    }

    updatePrice(variant) {
      if (!this.priceRoot) return;
      const priceEl = this.priceRoot;
      const effectiveCompareAtPrice = this.getEffectiveCompareAtPrice(variant);
      const sale = effectiveCompareAtPrice && effectiveCompareAtPrice > variant.price;
      priceEl.classList.toggle('price--on-sale', Boolean(sale));
      priceEl.classList.toggle('price--sold-out', !variant.available);

      const regularEl = priceEl.querySelector('.price__regular .price-item--regular');
      if (regularEl) {
        regularEl.childNodes.forEach(node => {
          if (node.nodeType === Node.TEXT_NODE) node.textContent = '';
        });
        let formatted = window.Shopify && typeof Shopify.formatMoney === 'function'
          ? Shopify.formatMoney(variant.price)
          : `${(variant.price / 100).toFixed(2)}`;
        if (priceEl.dataset.euroBeforePrice && formatted.includes('€')) {
          console.log('euro before price');
          formatted = formatted.replace('€', '');
          formatted = '€' + formatted;
        }
        // If there is a value span appended after text, keep it; otherwise just set text
        const valueSpan = regularEl.querySelector('.price-item--value');
        if (valueSpan) {
          regularEl.firstChild ? regularEl.firstChild.textContent = formatted + ' ' : regularEl.insertBefore(document.createTextNode(formatted + ' '), regularEl.firstChild);
        } else {
          regularEl.textContent = formatted;
        }
      }

      const salePriceEl = priceEl.querySelector('.price__sale .price-item--sale');
      const compareEl = priceEl.querySelector('.price__sale s');
      if (salePriceEl) {
        let formattedSale = window.Shopify && typeof Shopify.formatMoney === 'function'
          ? Shopify.formatMoney(variant.price)
          : `${(variant.price / 100).toFixed(2)}`;
        if (priceEl.dataset.euroBeforePrice && formattedSale.includes('€')) {
          formattedSale = formattedSale.replace('€', '');
          formattedSale = '€' + formattedSale;
        }
        salePriceEl.textContent = formattedSale;
      }
      if (compareEl) {
        let formattedCompare = window.Shopify && typeof Shopify.formatMoney === 'function'
          ? Shopify.formatMoney(effectiveCompareAtPrice || variant.price)
          : `${(((effectiveCompareAtPrice || variant.price) / 100).toFixed(2))}`;
        if (priceEl.dataset.euroBeforePrice && formattedCompare.includes('€')) {
          formattedCompare = formattedCompare.replace('€', '');
          formattedCompare = '€' + formattedCompare;
        }
        compareEl.textContent = formattedCompare;
      }
    }

    updateAvailabilityStates() {
      if (!this.variantsData.length) return;
      // Determine unique option indices present in the UI
      const indices = new Set();
      this.optionInputs.forEach(i => indices.add(parseInt(i.dataset.optionIndex, 10) || 1));
      this.colorSwatches.forEach(s => indices.add(parseInt(s.dataset.optionIndex, 10) || 1));

      const selections = this.getSelectedOptions();

      // For each option group, compute available values given other selections
      indices.forEach((groupIndex) => {
        const idx0 = groupIndex - 1;
        const selectedForGroup = selections[idx0];
        const otherSelections = selections.slice();
        otherSelections[idx0] = null;

        const matchingVariants = this.variantsData.filter(v => {
          return otherSelections.every((val, i) => val == null || v[`option${i+1}`] === val);
        });
        const availableValues = Array.from(new Set(matchingVariants.map(v => v[`option${groupIndex}`])));

        // Apply to color swatches
        const colorEls = Array.from(this.colorSwatches).filter(s => (parseInt(s.dataset.optionIndex, 10) || 1) === groupIndex);
        colorEls.forEach(swatch => {
          const isAvailable = availableValues.includes(swatch.dataset.optionValue);
          swatch.disabled = !isAvailable;
          swatch.setAttribute('aria-disabled', String(!isAvailable));
          swatch.classList.toggle('opacity-50', !isAvailable);
          swatch.classList.toggle('cursor-not-allowed', !isAvailable);
          swatch.classList.toggle('pointer-events-none', !isAvailable);
        });

        // Apply to radio inputs for non-color options
        const radioEls = Array.from(this.optionInputs).filter(i => (parseInt(i.dataset.optionIndex, 10) || 1) === groupIndex);
        let hasChecked = false;
        radioEls.forEach(input => {
          const isAvailable = availableValues.includes(input.value);
          input.disabled = !isAvailable;
          if (input.checked) hasChecked = input.checked && isAvailable;
          const label = input.closest('label');
          if (label) {
            label.classList.toggle('opacity-50', !isAvailable);
            label.classList.toggle('cursor-not-allowed', !isAvailable);
            label.classList.toggle('pointer-events-none', !isAvailable);
          }
        });

        // If the current selection for this group is not available, auto-select the first available
        if (!hasChecked && radioEls.length) {
          const firstAvailable = radioEls.find(i => !i.disabled);
          if (firstAvailable) {
            firstAvailable.checked = true;
            const selectedValueTextEl = firstAvailable.closest('fieldset')?.querySelector('[data-selected-value]');
            if (selectedValueTextEl) selectedValueTextEl.textContent = firstAvailable.value;
          }
        }

        // For color, if active is disabled, switch to first available
        if (colorEls.length) {
          const active = colorEls.find(s => s.classList.contains('active'));
          if (!active || active.disabled) {
            const firstAvailable = colorEls.find(s => !s.disabled);
            if (firstAvailable) {
              colorEls.forEach(s => s.classList.remove('active'));
              firstAvailable.classList.add('active');
            }
          }
        }
      });

      // After adjustments, ensure UI reflects a valid variant
      const newSelections = this.getSelectedOptions();
      const variant = this.findBestVariantFromSelections(newSelections);
      this.updateUiForVariant(variant, { skipAvailability: true });
    }

    updateButtonState(isAvailable) {
      if (this.addToCartButton) {
        this.addToCartButton.disabled = !isAvailable;
        this.addToCartButton.classList.toggle('loading', false);
      }

      if (this.buttonText) {
        this.buttonText.textContent = isAvailable 
          ? this.addCartText
          : this.soldOutText;
      }
    }
  }

  customElements.define('product-card', ProductCard);
}
